
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Plus, Edit, Users, Calendar, User, DollarSign, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { EmployeeProfileForm } from "../profile/EmployeeProfileForm";
import { EmployeePageLayout } from "../core/EmployeePageLayout";

interface Employee {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  hire_date: string;
  is_active: boolean;
  role: 'staff' | 'admin';
  created_at: string;
  employee_profiles?: any[];
}

interface EmployeeManagementProps {
  onBack: () => void;
  onViewTimeCalendar: () => void;
}

export const EmployeeManagement = ({ onBack, onViewTimeCalendar }: EmployeeManagementProps) => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEmployeeProfile, setSelectedEmployeeProfile] = useState<Employee | null>(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    hire_date: new Date().toISOString().split('T')[0],
    role: 'staff' as 'staff' | 'admin'
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          *,
          employee_profiles(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEmployees((data || []) as unknown as Employee[]);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast({
        title: "Error",
        description: "Failed to fetch employees.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.employee_id || !formData.first_name || !formData.last_name) {
      toast({
        title: "Required Fields Missing",
        description: "Employee ID, first name, and last name are required.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      if (editingEmployee) {
        const { error } = await supabase
          .from('employees')
          .update({
            employee_id: formData.employee_id,
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email || null,
            phone: formData.phone || null,
            hire_date: formData.hire_date,
            role: formData.role
          })
          .eq('id', editingEmployee.id);

        if (error) throw error;

        toast({
          title: "Employee Updated",
          description: "Employee information has been updated successfully.",
        });
      } else {
        const { data: newEmployee, error } = await supabase
          .from('employees')
          .insert([{
            employee_id: formData.employee_id,
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email || null,
            phone: formData.phone || null,
            hire_date: formData.hire_date,
            role: formData.role
          }])
          .select()
          .single();

        if (error) throw error;

        // Create default employee profile
        try {
          await supabase
            .from('employee_profiles')
            .insert([{
              employee_id: newEmployee.id,
              hourly_rate: 15.00,
              overtime_rate: 22.50,
              salary_type: 'hourly',
              payment_method: 'direct_deposit'
            }]);
        } catch (profileError) {
          console.error('Error creating employee profile:', profileError);
          toast({
            title: "Employee Added",
            description: "Employee created but please set up their wage profile.",
          });
        }

        toast({
          title: "Employee Added",
          description: "New employee has been added successfully.",
        });
      }

      resetForm();
      setIsDialogOpen(false);
      fetchEmployees();
    } catch (error: any) {
      console.error('Error saving employee:', error);
      toast({
        title: "Error",
        description: error.message?.includes('duplicate') 
          ? "Employee ID already exists." 
          : "Failed to save employee information.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleEmployeeStatus = async (employee: Employee) => {
    try {
      const { error } = await supabase
        .from('employees')
        .update({ is_active: !employee.is_active })
        .eq('id', employee.id);

      if (error) throw error;

      toast({
        title: employee.is_active ? "Employee Deactivated" : "Employee Activated",
        description: `${employee.first_name} ${employee.last_name} has been ${employee.is_active ? 'deactivated' : 'activated'}.`,
      });

      fetchEmployees();
    } catch (error) {
      console.error('Error updating employee status:', error);
      toast({
        title: "Error",
        description: "Failed to update employee status.",
        variant: "destructive"
      });
    }
  };

  const updateEmployeeRole = async (employee: Employee, newRole: 'staff' | 'admin') => {
    try {
      const { error } = await supabase.rpc('update_employee_role', {
        emp_id: employee.id,
        new_role: newRole
      });

      if (error) throw error;

      toast({
        title: "Role Updated",
        description: `${employee.first_name} ${employee.last_name} is now ${newRole === 'admin' ? 'an Administrator' : 'Staff'}.`,
      });

      fetchEmployees();
    } catch (error: any) {
      console.error('Error updating employee role:', error);
      toast({
        title: "Error",
        description: error.message?.includes('last admin') 
          ? "Cannot demote the last admin. At least one admin must exist."
          : "Failed to update employee role.",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      hire_date: new Date().toISOString().split('T')[0],
      role: 'staff' as 'staff' | 'admin'
    });
    setEditingEmployee(null);
  };

  const openEditDialog = (employee: Employee) => {
    setEditingEmployee(employee);
    setFormData({
      employee_id: employee.employee_id,
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email || "",
      phone: employee.phone || "",
      hire_date: employee.hire_date,
      role: employee.role
    });
    setIsDialogOpen(true);
  };

  const filteredEmployees = employees.filter(employee =>
    employee.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.employee_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <EmployeePageLayout
      title="Employee Management"
      subtitle="Manage employee profiles and access"
      onBack={onBack}
      actions={
        <Button 
          onClick={onViewTimeCalendar}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Calendar className="w-4 h-4" />
          Time Calendar
        </Button>
      }
    >
      <div className="space-y-6">

      {/* Actions Bar */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <Input
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1"
        />
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingEmployee ? "Edit Employee" : "Add New Employee"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="employee_id">Employee ID</Label>
                  <Input
                    id="employee_id"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="hire_date">Hire Date</Label>
                  <Input
                    id="hire_date"
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(value: 'staff' | 'admin') => setFormData({...formData, role: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? "Saving..." : editingEmployee ? "Update" : "Add"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Employee List */}
      <div className="space-y-4">
        {isLoading ? (
          <Card>
            <CardContent className="flex items-center justify-center p-8">
              <p>Loading employees...</p>
            </CardContent>
          </Card>
        ) : filteredEmployees.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center p-8">
              <Users className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No employees found</p>
              {searchTerm && (
                <p className="text-sm text-muted-foreground mt-2">
                  Try adjusting your search terms
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredEmployees.map((employee) => (
            <Card key={employee.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
                  {/* Employee Info */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="font-semibold text-lg">
                          {employee.first_name} {employee.last_name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>ID: {employee.employee_id}</span>
                          <span>•</span>
                          <span>Hired: {new Date(employee.hire_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Contact & Wage Info */}
                    <div className="flex flex-wrap gap-4 text-sm">
                      {employee.email && (
                        <div className="text-muted-foreground">
                          <span className="font-medium">Email:</span> {employee.email}
                        </div>
                      )}
                      {employee.phone && (
                        <div className="text-muted-foreground">
                          <span className="font-medium">Phone:</span> {employee.phone}
                        </div>
                      )}
                      {employee.employee_profiles?.[0] && (
                        <div className="text-green-600">
                          <span className="font-medium">Rate:</span> ${employee.employee_profiles[0].hourly_rate || 'Not set'}/hr
                        </div>
                      )}
                    </div>

                    {/* Status Badges */}
                    <div className="flex items-center gap-2">
                      <Badge variant={employee.is_active ? "default" : "secondary"}>
                        {employee.is_active ? "Active" : "Inactive"}
                      </Badge>
                      <Badge 
                        variant={employee.role === 'admin' ? "destructive" : "outline"}
                      >
                        {employee.role === 'admin' ? (
                          <>
                            <Shield className="w-3 h-3 mr-1" />
                            Administrator
                          </>
                        ) : (
                          'Staff'
                        )}
                      </Badge>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
                    {/* Primary Actions */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setSelectedEmployeeProfile(employee)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1"
                      >
                        <DollarSign className="w-3 h-3" />
                        Wage
                      </Button>
                      <Button
                        onClick={() => navigate(`/employee-profile/${employee.id}`)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1"
                      >
                        <User className="w-3 h-3" />
                        Profile
                      </Button>
                      <Button
                        onClick={() => openEditDialog(employee)}
                        variant="outline"
                        size="sm"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Role & Status Actions */}
                    <div className="flex gap-2">
                      {employee.role === 'staff' && (
                        <Button
                          onClick={() => updateEmployeeRole(employee, 'admin')}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                        >
                          Make Admin
                        </Button>
                      )}
                      {employee.role === 'admin' && (
                        <Button
                          onClick={() => updateEmployeeRole(employee, 'staff')}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                        >
                          Remove Admin
                        </Button>
                      )}
                      <Button
                        onClick={() => toggleEmployeeStatus(employee)}
                        variant={employee.is_active ? "destructive" : "default"}
                        size="sm"
                      >
                        {employee.is_active ? "Deactivate" : "Activate"}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {selectedEmployeeProfile && (
        <EmployeeProfileForm
          employee={selectedEmployeeProfile}
          open={!!selectedEmployeeProfile}
          onOpenChange={() => setSelectedEmployeeProfile(null)}
          onSuccess={fetchEmployees}
        />
      )}
      </div>
    </EmployeePageLayout>
  );
};
